import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { deleteApiKey, archiveOldRecords, backfillPhones, getPlanConfig, saveApiKey, setPlanConfig } from "@/lib/api";
import { Archive, DollarSign, KeyRound, Loader2, Phone, Trash2 } from "lucide-react";

export default function ApiKeyDialog({ open, onOpenChange, apiKeyState, onSaved }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [planValue, setPlanValue] = useState("");
  const [planCurrent, setPlanCurrent] = useState(null);
  const [planSaving, setPlanSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveDays, setArchiveDays] = useState("30");
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  useEffect(() => {
    if (open) {
      setValue("");
      getPlanConfig()
        .then((d) => {
          setPlanCurrent(d);
          setPlanValue(String(d.plan_usd_monthly ?? ""));
        })
        .catch(() => {
          setPlanCurrent(null);
          setPlanValue("");
        });
    }
  }, [open]);

  const onSave = async () => {
    if (!value.trim()) {
      toast.error("Enter an API key");
      return;
    }
    setSaving(true);
    try {
      await saveApiKey(value.trim());
      toast.success("API key saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save key";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async () => {
    if (!confirm("Remove stored ElevenLabs API key?")) return;
    try {
      await deleteApiKey();
      toast.success("API key removed");
      onSaved?.();
    } catch {
      toast.error("Failed to remove key");
    }
  };

  const onSavePlan = async () => {
    const n = Number(planValue);
    if (!isFinite(n) || n < 0) {
      toast.error("Enter a valid plan cost (USD)");
      return;
    }
    setPlanSaving(true);
    try {
      await setPlanConfig(n);
      toast.success(`Plan cost set to $${n.toFixed(2)}/mo`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save plan cost");
    } finally {
      setPlanSaving(false);
    }
  };

  const onArchive = async () => {
    const n = Number(archiveDays);
    if (!isFinite(n) || n < 1) {
      toast.error("Enter valid days (>=1)");
      return;
    }
    if (!confirm(`Slim records older than ${n} days? Raw payloads dropped, transcript & metadata kept.`)) return;
    setArchiving(true);
    try {
      const r = await archiveOldRecords(n);
      toast.success(`Archived ${r.archived} records (older than ${n}d)`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    } finally {
      setArchiving(false);
    }
  };

  const onBackfill = async () => {
    if (!confirm("Backfill phone numbers from stored raw_payload? Scans all non-archived records once. Safe to re-run.")) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const r = await backfillPhones();
      setBackfillResult(r);
      toast.success(`Backfilled ${r.updated} of ${r.scanned} records with phone numbers`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="api-key-dialog"
        className="rounded-none bg-white border border-black/15 text-black sm:max-w-lg p-0 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-black/10 space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border border-black/25 grid place-items-center">
              <KeyRound className="w-4 h-4 text-black" />
            </div>
            <div>
              <DialogTitle className="font-heading text-xl font-light tracking-tight text-black">
                ElevenLabs Settings
              </DialogTitle>
              <DialogDescription className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/55">
                api key · monthly plan cost · workspace-scoped
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 py-5 space-y-6">
          {/* API key section */}
          <div className="space-y-3">
            <h4 className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/55">
              ElevenLabs API key
            </h4>
            {apiKeyState?.connected && (
              <div
                className="flex items-center justify-between p-3 border border-black/10 bg-black/[0.02]"
                data-testid="current-key-status"
              >
                <div className="flex flex-col">
                  <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/55">
                    Connected key
                  </span>
                  <span className="font-mono-plex text-sm text-black">{apiKeyState.masked_key}</span>
                </div>
                <Button
                  data-testid="remove-key-button"
                  variant="outline"
                  size="sm"
                  onClick={onRemove}
                  className="h-8 rounded-none border-black/15 bg-white text-[#DC2626] hover:bg-[#DC2626]/10 hover:text-[#DC2626] font-mono-plex text-[10px] uppercase tracking-[0.15em]"
                >
                  <Trash2 className="w-3 h-3 mr-1.5" />
                  Remove
                </Button>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Input
                data-testid="api-key-input"
                type="password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={apiKeyState?.connected ? "paste new key to replace…" : "sk_..."}
                className="h-10 rounded-none bg-white border-black/20 text-black placeholder:text-black/30 font-mono-plex text-sm focus-visible:ring-1 focus-visible:ring-black/30 focus-visible:border-black/50"
                onKeyDown={(e) => e.key === "Enter" && onSave()}
              />
              <div className="flex justify-end">
                <Button
                  data-testid="save-api-key"
                  onClick={onSave}
                  disabled={saving || !value.trim()}
                  className="h-9 rounded-none bg-black text-white hover:bg-black/85 font-mono-plex text-[11px] uppercase tracking-[0.15em]"
                >
                  {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
                  {apiKeyState?.connected ? "Replace key" : "Save & verify"}
                </Button>
              </div>
              <p className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/45 leading-relaxed">
                Get one at elevenlabs.io/app/settings/api-keys · validated against /v1/convai/conversations
              </p>
            </div>
          </div>

          <div className="border-t border-black/10" />

          {/* Plan cost section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-3.5 h-3.5 text-black/60" />
              <h4 className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/55">
                Monthly plan cost (USD)
              </h4>
            </div>
            <div className="flex items-stretch gap-2">
              <Input
                data-testid="plan-cost-input"
                type="number"
                min="0"
                step="0.01"
                value={planValue}
                onChange={(e) => setPlanValue(e.target.value)}
                placeholder="1012.03"
                className="h-10 rounded-none bg-white border-black/20 text-black placeholder:text-black/30 font-mono-plex text-sm focus-visible:ring-1 focus-visible:ring-black/30 focus-visible:border-black/50 flex-1"
                onKeyDown={(e) => e.key === "Enter" && onSavePlan()}
              />
              <Button
                data-testid="save-plan-cost"
                onClick={onSavePlan}
                disabled={planSaving || planValue === ""}
                className="h-10 rounded-none bg-black text-white hover:bg-black/85 font-mono-plex text-[11px] uppercase tracking-[0.15em]"
              >
                {planSaving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
                Save
              </Button>
            </div>
            <p className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/45 leading-relaxed">
              {planCurrent
                ? `current · $${Number(planCurrent.plan_usd_monthly).toFixed(2)} · source: ${planCurrent.source}${planCurrent.updated_at ? " · updated " + new Date(planCurrent.updated_at).toLocaleDateString() : ""}`
                : "loading current value…"}
              <br />
              persisted in mongodb · survives redeploys · no env var change needed
            </p>
          </div>

          <div className="border-t border-black/10" />

          {/* Archive section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Archive className="w-3.5 h-3.5 text-black/60" />
              <h4 className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/55">
                Slim old records
              </h4>
            </div>
            <div className="flex items-stretch gap-2">
              <Input
                data-testid="archive-days-input"
                type="number"
                min="1"
                value={archiveDays}
                onChange={(e) => setArchiveDays(e.target.value)}
                placeholder="30"
                className="h-10 rounded-none bg-white border-black/20 text-black placeholder:text-black/30 font-mono-plex text-sm focus-visible:ring-1 focus-visible:ring-black/30 focus-visible:border-black/50 flex-1"
              />
              <Button
                data-testid="archive-old-button"
                onClick={onArchive}
                disabled={archiving}
                className="h-10 rounded-none bg-black text-white hover:bg-black/85 font-mono-plex text-[11px] uppercase tracking-[0.15em]"
              >
                {archiving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
                Archive
              </Button>
            </div>
            <p className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/45 leading-relaxed">
              Drops `raw_payload`, `raw_list_item`, `raw_messages` for records older than N days. Keeps title, transcript, costs, and all metadata. Month-wise browsing and CSV export still work. Reduces per-record storage by ~80%.
            </p>
          </div>

          <div className="border-t border-black/10" />

          {/* Backfill phones section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-black/60" />
              <h4 className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/55">
                Backfill phone numbers
              </h4>
            </div>
            <div className="flex items-stretch gap-2">
              <Button
                data-testid="backfill-phones-button"
                onClick={onBackfill}
                disabled={backfilling}
                className="h-10 rounded-none bg-black text-white hover:bg-black/85 font-mono-plex text-[11px] uppercase tracking-[0.15em] flex-1"
              >
                {backfilling && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
                Extract phones from history
              </Button>
            </div>
            {backfillResult && (
              <p
                data-testid="backfill-phones-result"
                className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-[#16A34A] leading-relaxed"
              >
                Backfilled {backfillResult.updated?.toLocaleString?.() ?? backfillResult.updated} of {backfillResult.scanned?.toLocaleString?.() ?? backfillResult.scanned} records. Reload User Database tab.
              </p>
            )}
            <p className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/45 leading-relaxed">
              One-time repair for records synced before the WhatsApp phone-number fix. Reads `raw_payload.metadata.whatsapp.whatsapp_user_id` and sets it as the caller's phone. Skips already-archived records (their raw payload was stripped). Safe to re-run — idempotent.
            </p>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-black/10 gap-2">
          <Button
            data-testid="cancel-api-key"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-9 rounded-none text-black/60 hover:text-black hover:bg-black/5 font-mono-plex text-[11px] uppercase tracking-[0.15em]"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
